import React, { useMemo, useState } from "react";
import { FaUser } from "react-icons/fa6";
import { format } from "date-fns";
import DayEventsModal from "./DayEventsModal";


const pad = n => String(n).padStart(2, "0");
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function buildMonthMatrix(anchorDate) {
  const y = anchorDate.getFullYear();
  const m = anchorDate.getMonth();

  const first = new Date(y, m, 1);
  const firstDow = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();


  const weeks = Math.ceil((firstDow + daysInMonth) / 7);
  const cells = weeks * 7;


  const start = new Date(first);
  start.setDate(1 - firstDow);

  const days = [];
  for (let i = 0; i < cells; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}


function formatRange(startISO, endISO) {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const s = format(start, "h:mm a", { timeZone: "Asia/Kolkata" }).toLowerCase();
  const e = endISO ? format(end, "h:mm a", { timeZone: "Asia/Kolkata" }).toLowerCase() : "";
  return e ? `${s} – ${e}` : s;
}


function normalizeAssignees(list = []) {
  return list.map(a => (typeof a === "object" ? a : { name: String(a) }));
}


export default function MonthGrid({
  monthDate = new Date(),
  events = [],
  colors = {},
  hiddenTypes = new Set(),
  onEventClick = () => { },
  avatarMode = "icon"
}) {
  const [viewMoreDay, setViewMoreDay] = useState(null);
  const [viewMoreEvents, setViewMoreEvents] = useState([]);

  const TYPE_CLASS = {
    MEETING: 'meeting',
    MENU_UPDATE: 'menu_update',
    INVENTORY_CHECK: 'inventory_check',
    EVENT: 'event',
    APPOINTMENT: 'appointment',
  };
  const typeClass = (t) => TYPE_CLASS[t] || 'default';
  const isDarkType = (t) => t === 'EVENT';

  const matrix = useMemo(() => buildMonthMatrix(monthDate), [monthDate]);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      if (hiddenTypes.has(e.type)) continue;
      const key = ymd(new Date(e.start));
      (m[key] ||= []).push(e);
    }
    Object.values(m).forEach(arr => arr.sort((a, b) => new Date(a.start) - new Date(b.start)));
    return m;
  }, [events, hiddenTypes]);

  const handleViewMore = (day, dayEvents, e) => {
    e.stopPropagation();
    setViewMoreDay(day);
    setViewMoreEvents(dayEvents);
  };

  const handleViewMoreEventClick = (ev) => {
    onEventClick(ev);
    setViewMoreDay(null);
    setViewMoreEvents([]);
  };

  const handleViewMoreClose = () => {
    setViewMoreDay(null);
    setViewMoreEvents([]);
  };

  return (
    <div className="mg">
      {matrix.map((day) => {
        const key = ymd(day);
        const list = eventsByDay[key] || [];
        const isOtherMonth = day.getMonth() !== monthDate.getMonth();
        const mode = list.length === 1 ? "single" : (list.length >= 2 ? "stacked" : "empty");

        return (
          <div key={key} className={`mg-cell${isOtherMonth ? " is-dim" : ""}`}>
            <div className="mg-cell-top">
              <span className="mg-daynum">{day.getDate()}</span>
            </div>

            <div className={`mg-events ${mode}`}>

              {mode === "single" && (() => {
                const ev = list[0];
                const cls = typeClass(ev.type);
                const dark = isDarkType(ev.type);
                const important = ev.importance === true || ev.importance === "important";
                const people = normalizeAssignees(ev.assignees || ev.participants || []);
                return (
                  <button
                    className={`mg-evt mg-evt--large mg-evt--${cls}${dark ? " is-dark" : ""}${important ? " is-important" : ""}`}
                    onClick={() => onEventClick(ev)}
                    aria-label={ev.title}
                    title={ev.title}
                  >
                    {important && <span className="mg-evt-star" aria-hidden="true">★</span>}
                    <div className="mg-evt-title two-lines">{ev.title}</div>
                    <div className="mg-evt-time">{formatRange(ev.start, ev.end)}</div>

                    {!!people.length && (
                      <div className="mg-evt-people">
                        {people.slice(0, 2).map((p, i) => (
                          <span key={i} className="mg-avatar">
                            <FaUser className="mg-avatar-ico" />
                          </span>
                        ))}
                        {people.length > 2 && (
                          <span className="mg-more">+{people.length - 2} others</span>
                        )}
                      </div>
                    )}


                  </button>
                );
              })()}

              {mode === "stacked" && (
                <div className="mg-stacked">
                  {list.slice(0, 2).map((ev) => {
                    const cls = typeClass(ev.type);
                    const dark = isDarkType(ev.type);
                    const important = ev.importance === true || ev.importance === "important";
                    return (
                      <button
                        key={ev.id}
                        className={`mg-evt mg-evt--compact mg-evt--${cls}${dark ? " is-dark" : ""}${important ? " is-important" : ""}`}
                        onClick={() => onEventClick(ev)}
                        aria-label={ev.title}
                        title={ev.title}
                      >
                        {important && <span className="mg-evt-star sm" aria-hidden="true">★</span>}
                        <div className="mg-evt-compact-body">
                          <div className="mg-evt-title one-line">{ev.title || "(no title)"}</div>
                          <div className="mg-evt-time sm">{formatRange(ev.start, ev.end)}</div>
                        </div>
                      </button>
                    );
                  })}
                  {list.length > 2 && (
                    <button 
                      className="mg-view-more" 
                      onClick={(e) => handleViewMore(day, list, e)}
                    >
                      + View More ({list.length - 2})
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <DayEventsModal
        isOpen={!!viewMoreDay}
        onClose={handleViewMoreClose}
        events={viewMoreEvents}
        date={viewMoreDay}
        onEventClick={handleViewMoreEventClick}
      />
    </div>
  );


}
